import { Switch, FormControlLabel, TextField, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AutocompleteFilter } from "./AutocompleteFilter";
import { useState, useEffect, useRef } from "react";

export interface ProductFilterState {
  productCode?: string | number;
  productName?: string;
  productGroupCode?: string | number;
  ingangsdatum?: string;
  gewasnummer?: string | number;
  showExpired?: boolean;
  showPrimaryCharacteristic?: boolean;
}

interface ProductFiltersProps {
  filters: ProductFilterState;
  onFilterChange: (changed: Partial<ProductFilterState>) => void;
}

export function ProductFilters({
  filters,
  onFilterChange,
}: ProductFiltersProps) {
  const { t } = useTranslation();

  // Lokale state voor ingangsdatum om visuele feedback tijdens typen te behouden
  const [localIngangsdatum, setLocalIngangsdatum] = useState<Dayjs | null>(
    filters.ingangsdatum ? dayjs(filters.ingangsdatum) : null
  );

  // Sync lokale state met filter changes van buitenaf
  useEffect(() => {
    const newValue = filters.ingangsdatum ? dayjs(filters.ingangsdatum) : null;
    setLocalIngangsdatum(newValue);
  }, [filters.ingangsdatum]);

  return (
    <Stack spacing={2}>
      <AutocompleteFilter
        filterName="productCode"
        label={t("product.columns.productCode")}
        value={filters.productCode ?? null}
        onChange={(value) =>
          onFilterChange({ productCode: value ? String(value) : undefined })
        }
        placeholder="Search product code..."
      />

      <AutocompleteFilter
        filterName="productName"
        label={t("product.columns.productName")}
        value={filters.productName ?? null}
        onChange={(value, option) => {
          // If a product is selected from dropdown (option exists with productCode),
          // filter by productCode instead of productName
          if (option && option.productCode) {
            onFilterChange({
              productCode: String(option.productCode),
              productName: undefined, // Clear productName filter when selecting from dropdown
            });
          } else {
            // If typing free text or cleared
            onFilterChange({
              productName: value ? String(value) : undefined,
              productCode: undefined, // Clear productCode filter when typing free text
            });
          }
        }}
        placeholder="Search product name..."
      />

      <AutocompleteFilter
        filterName="productGroupCode"
        label={t("product.columns.productGroupCode")}
        value={filters.productGroupCode ?? null}
        onChange={(value) =>
          onFilterChange({
            productGroupCode: value ? String(value) : undefined,
          })
        }
        placeholder="Search product group..."
      />

      <DatePicker
        key={filters.ingangsdatum || 'empty'}
        format="DD/MM/YYYY"
        label={t("product.columns.ingangsdatum")}
        value={localIngangsdatum}
        onChange={(newValue: Dayjs | null) => {
          setLocalIngangsdatum(newValue);

          if (newValue?.isValid() && newValue.year() >= 1900) {
            onFilterChange({
              ingangsdatum: newValue.toISOString(),
            });
          } else if (newValue === null) {
            onFilterChange({ ingangsdatum: undefined });
          }
        }}
        slotProps={{
          textField: {
            size: "small",
            onBlur: () => {
              if (localIngangsdatum?.isValid() && localIngangsdatum.year() >= 1900) {
                onFilterChange({
                  ingangsdatum: localIngangsdatum.toISOString(),
                });
              }
            },
            onKeyDown: (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (localIngangsdatum?.isValid() && localIngangsdatum.year() >= 1900) {
                  onFilterChange({
                    ingangsdatum: localIngangsdatum.toISOString(),
                  });
                }
              }
            }
          },
          field: {
            clearable: true,
            onClear: () => {
              setLocalIngangsdatum(null);
              onFilterChange({ ingangsdatum: undefined });
            }
          },
        }}
      />

      <AutocompleteFilter
        filterName="gewasnummer"
        label={t("product.columns.gewascode")}
        value={filters.gewasnummer ?? null}
        onChange={(value) =>
          onFilterChange({ gewasnummer: value ? String(value) : undefined })
        }
        placeholder="Search taxonomic number..."
      />

      <FormControlLabel
        control={
          <Switch
            checked={filters.showExpired ?? false}
            onChange={(_, checked) => onFilterChange({ showExpired: checked })}
          />
        }
        label={t("product.filters.showExpired_label")}
      />

      <FormControlLabel
        control={
          <Switch
            checked={filters.showPrimaryCharacteristic ?? false}
            onChange={(_, checked) =>
              onFilterChange({ showPrimaryCharacteristic: checked })
            }
          />
        }
        label={t("product.filters.showPrimaryCharacteristic_label")}
      />
    </Stack>
  );
}
